import { post as httpPost } from '@/services/http';

/**
 * AS-11685 — live Shopify stock via assisty-bot `POST /inventory/levels`
 * (parent AS-11682 D1). Mirrors atlas `domain/inventory-levels` (AS-11684).
 */

// The bot rejects a request with more than this many items (MAX_ITEMS).
export const INVENTORY_LEVELS_MAX_ITEMS = 500;

export type InventoryQuantityName = 'available' | 'on_hand';

export interface LiveLevels {
  available: number | null;
  onHand: number | null;
}

interface PostDep { post: (url: string, body?: any) => Promise<any>; }

const asQuantity = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * The one "is this a usable inventory item id" rule, matching the bot's
 * isNumericId (positive safe integers only): anything else makes the bot 400
 * the whole chunk. BIGINT ids arrive as strings.
 */
export function parseInventoryItemId(value: unknown): number | null {
  if (value == null || value === '') return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** Usable ids only, once each. */
export function toInventoryItemIds(raw: ReadonlyArray<unknown>): number[] {
  const seen = new Set<number>();
  for (const value of raw) {
    const id = parseInventoryItemId(value);
    if (id != null) seen.add(id);
  }
  return [...seen];
}

/**
 * Levels keyed by inventory item id. An id missing from the result means
 * Shopify has no level for it at that location. All-or-nothing: if any chunk
 * fails the whole read rejects, so the caller never shows a half-loaded table.
 */
export async function fetchInventoryLevels(
  locationId: number,
  inventoryItemIds: ReadonlyArray<unknown>,
  names: InventoryQuantityName[],
  deps: PostDep = { post: httpPost },
): Promise<Record<number, LiveLevels>> {
  const ids = toInventoryItemIds(inventoryItemIds);
  if (ids.length === 0) return {};

  const chunks: number[][] = [];
  for (let i = 0; i < ids.length; i += INVENTORY_LEVELS_MAX_ITEMS) {
    chunks.push(ids.slice(i, i + INVENTORY_LEVELS_MAX_ITEMS));
  }
  const bodies = await Promise.all(chunks.map((chunk) => deps.post('/inventory/levels', {
    locationId,
    items: chunk.map((inventoryItemId) => ({ inventoryItemId })),
    names,
  })));

  const levels: Record<number, LiveLevels> = {};
  for (const body of bodies) {
    for (const row of body?.data?.levels ?? []) {
      const id = parseInventoryItemId(row?.inventory_item_id);
      if (id == null) continue;
      const q = row.quantities ?? {};
      levels[id] = { available: asQuantity(q.available), onHand: asQuantity(q.on_hand) };
    }
  }
  return levels;
}
